/**
 * Robot-telemetry slice — ingests DataChannel `telemetry` events.
 *
 * Keeps the typed dog-style snapshot for unused widgets (SpeedHud, cargo) and
 * an adaptive `getFields()` list for the generic tiles.
 *
 * Framework-free. Owns last status, fields, age, cargo-lid normalization.
 */

import { createStore } from "./store";
import type { StoreApi } from "./store";
import { normalizeTelemetryFields } from "./telemetry-fields";
import type { TelemetryField } from "./telemetry-fields";

export interface CargoLidEntry {
  id: string;
  lid: "open" | "closed" | "unknown";
}

export interface RobotStatusPayload {
  battery_percent?: number;
  mode?: string;
  timestamp?: number;
  motion_state?: string;
  velocity?: { vx?: number; vy?: number; vyaw?: number; steer_angle?: number };
  cargo?: CargoLidEntry[];
}

export interface RobotTelemetrySlice extends Pick<
  StoreApi<RobotStatusPayload | null>,
  "getState" | "getInitialState" | "subscribe"
> {
  /** ms since last telemetry packet */
  getAgeMs(): number | null;
  /** Adaptive tiles — last telemetry payload as a list of dicts. */
  getFields(): TelemetryField[];
  ingest(message: unknown): void;
  reset(): void;
}

function normalizeCargoTelemetry(raw: unknown): CargoLidEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: CargoLidEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const id = String(rec.id || "").trim();
    const lid = String(rec.lid || "unknown")
      .trim()
      .toLowerCase();
    if (!id) continue;
    const lidNorm: CargoLidEntry["lid"] =
      lid === "open" || lid === "closed" || lid === "unknown" ? lid : "unknown";
    out.push({ id, lid: lidNorm });
  }
  return out;
}

export function createRobotTelemetrySlice(): RobotTelemetrySlice {
  let lastTelemetryAt: number | null = null;
  let fields: TelemetryField[] = [];
  const store = createStore<RobotStatusPayload | null>(() => null);

  return {
    getState: store.getState,
    getInitialState: store.getInitialState,
    subscribe: store.subscribe,
    getAgeMs: () => (lastTelemetryAt == null ? null : Date.now() - lastTelemetryAt),
    getFields: () => fields,
    ingest(message) {
      let raw: unknown = message;
      if (typeof raw !== "string") {
        if (raw instanceof ArrayBuffer) raw = new TextDecoder().decode(new Uint8Array(raw));
        else if (ArrayBuffer.isView(raw)) raw = new TextDecoder().decode(raw);
        else raw = String(raw);
      }
      try {
        const parsed = JSON.parse(String(raw).trim()) as Record<string, unknown>;
        const event = parsed.event ?? parsed.type;
        if (event !== "telemetry") {
          return;
        }
        const payload = parsed.payload ?? parsed.data;
        if (!payload || typeof payload !== "object") return;
        const rec = payload as Record<string, unknown>;
        const robotStatus =
          rec.robot_status && typeof rec.robot_status === "object"
            ? (rec.robot_status as Record<string, unknown>)
            : {};
        const motionState =
          rec.motion_state && typeof rec.motion_state === "object"
            ? (rec.motion_state as Record<string, unknown>)
            : {};
        const velocity =
          motionState.velocity && typeof motionState.velocity === "object"
            ? motionState.velocity
            : undefined;
        lastTelemetryAt = Date.now();
        fields = normalizeTelemetryFields(payload);
        store.setState(
          {
            battery_percent: robotStatus.battery_percent as number | undefined,
            mode: robotStatus.mode as string | undefined,
            timestamp: rec.timestamp as number | undefined,
            motion_state: motionState.state as string | undefined,
            velocity: velocity as
              { vx?: number; vy?: number; vyaw?: number; steer_angle?: number } | undefined,
            cargo: normalizeCargoTelemetry(rec.cargo),
          },
          true
        );
      } catch {
        /* not JSON or unrelated event */
      }
    },
    reset() {
      lastTelemetryAt = null;
      fields = [];
      store.setState(null, true);
    },
  };
}

/** Look up the cargo lid state for a box id (pure helper, no store). */
export function lidForBox(
  cargo: CargoLidEntry[] | undefined,
  boxId: string
): "open" | "closed" | "unknown" {
  const key = String(boxId || "").trim();
  if (!key || !Array.isArray(cargo)) return "unknown";
  const row = cargo.find((c) => c.id === key);
  return row?.lid || "unknown";
}
